export type CapacityPlanContext = {
	view: string;
	scopeId: string;
};

export const CapacityPlanningActionType = {
	ADD_CONTRIBUTOR_WORK: 'ADD_CONTRIBUTOR_WORK',
	ADD_CONTRIBUTORS: 'ADD_CONTRIBUTORS',
	CREATE_NON_PROJECT_WORK: 'CREATE_NON_PROJECT_WORK',
	REMOVE_CONTRIBUTOR_WORK: 'REMOVE_CONTRIBUTOR_WORK',
	REMOVE_CONTRIBUTORS: 'REMOVE_CONTRIBUTORS',
	UPDATE_CONTRIBUTIONS: 'UPDATE_CONTRIBUTIONS',
	RENAME_NON_PROJECT_WORK: 'RENAME_NON_PROJECT_WORK',
	ADD_WORKS: 'ADD_WORKS',
	REMOVE_WORKS: 'REMOVE_WORKS',
} as const;

export type CapacityPlanningActionType =
	(typeof CapacityPlanningActionType)[keyof typeof CapacityPlanningActionType];

type ContributorWorkAssociation = {
	contributorDataId: string;
	workId: string;
};

type Contribution = {
	contributorDataId: string;
	startDate: string;
	endDate: string;
	value: number;
	workId: string;
	valueType: string;
};

/**
 * Please make sure that input types defined here match the input types defined in:
 * https://bitbucket.org/atlassian/conversational-ai-platform/src/main/modules/product/rovo/rovo-impl/src/main/kotlin/io/atlassian/micros/convoai/agent/playground/tools/inputdata/CapacityPlanManageOperationToolInput.kt
 */
type ContributionsInputs = {
	scopeId: string;
	contributions: Contribution[];
	contributorWorkAssociations: ContributorWorkAssociation[];
};

/**
 * Inputs for {@link CapacityPlanningActionType.ADD_WORKS} and {@link CapacityPlanningActionType.REMOVE_WORKS} —
 * Jira issue ARIs to add or remove as work rows.
 */
type WorksInputs = {
	workDataIds: string[];
	scopeId: string;
};

type ContributorScopeAssociationInputs = {
	contributorDataIds: string[];
	scopeId: string;
};

type ContributorWorkAssociationInputs = {
	contributorWorkAssociations: ContributorWorkAssociation[];
};

type CreateNonProjectWorkInputs = {
	contributorDataId: string;
	name: string;
};

type RenameNonProjectWorkInputs = {
	id: string;
	name: string;
};

export type CapacityPlanningPayload =
	| {
			action: typeof CapacityPlanningActionType.ADD_CONTRIBUTOR_WORK;
			inputs: ContributorWorkAssociationInputs;
			capacityPlanContext: CapacityPlanContext;
	  }
	| {
			action: typeof CapacityPlanningActionType.ADD_CONTRIBUTORS;
			inputs: ContributorScopeAssociationInputs;
			capacityPlanContext: CapacityPlanContext;
	  }
	| {
			action: typeof CapacityPlanningActionType.CREATE_NON_PROJECT_WORK;
			inputs: CreateNonProjectWorkInputs;
			capacityPlanContext: CapacityPlanContext;
	  }
	| {
			action: typeof CapacityPlanningActionType.REMOVE_CONTRIBUTOR_WORK;
			inputs: ContributorWorkAssociationInputs;
			capacityPlanContext: CapacityPlanContext;
	  }
	| {
			action: typeof CapacityPlanningActionType.REMOVE_CONTRIBUTORS;
			inputs: ContributorScopeAssociationInputs;
			capacityPlanContext: CapacityPlanContext;
	  }
	| {
			action: typeof CapacityPlanningActionType.UPDATE_CONTRIBUTIONS;
			inputs: ContributionsInputs;
			capacityPlanContext: CapacityPlanContext;
	  }
	| {
			action: typeof CapacityPlanningActionType.RENAME_NON_PROJECT_WORK;
			inputs: RenameNonProjectWorkInputs;
			capacityPlanContext: CapacityPlanContext;
	  }
	| {
			action: typeof CapacityPlanningActionType.ADD_WORKS;
			inputs: WorksInputs;
			capacityPlanContext: CapacityPlanContext;
	  }
	| {
			action: typeof CapacityPlanningActionType.REMOVE_WORKS;
			inputs: WorksInputs;
			capacityPlanContext: CapacityPlanContext;
	  };
